/**
 * Local response cache (per device) — makes the app open INSTANTLY by showing the
 * last-known data while a fresh copy loads in the background ("cache-first, then
 * revalidate"). Persisted to localStorage so it survives restarts.
 *
 * Invalidation: cleared on sign-out / invalid session (`clearAllCache`), can be
 * cleared on demand from the admin side, and is naturally gone when the app is
 * uninstalled (localStorage is wiped). Entries also carry a soft TTL so genuinely
 * stale data isn't shown forever if the network never returns.
 */
const PREFIX = "genz.cache.";
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// AR-08: sign-out cancels in-flight requests, but not a device run's, and a response that settled
// just before it can still resume after `clearAllCache` and re-seed the cache the next account reads.
// A writer captures the epoch before its await and passes it back; a stale epoch drops the write.
let sessionEpoch = 0;

export function cacheEpoch(): number {
  return sessionEpoch;
}

interface Entry<T> {
  v: 1;
  at: number;
  data: T;
}

function ls(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Read a cached value WITH the time it was written (or null when missing /
 * expired / unparseable).
 *
 * GEN-8: the write time is what lets a surface say "showing cached data from
 * <when>" instead of passing a 7-day-old copy off as live. Without it the UI has
 * no way to tell a value fetched a second ago from one fetched last week.
 */
export function readCacheEntry<T>(key: string, ttlMs = DEFAULT_TTL_MS): { data: T; at: number } | null {
  const store = ls();
  if (!store) return null;
  try {
    const raw = store.getItem(PREFIX + key);
    if (!raw) return null;
    const e = JSON.parse(raw) as Entry<T>;
    if (!e || e.v !== 1 || typeof e.at !== "number") return null;
    if (Date.now() - e.at > ttlMs) return null;
    return { data: e.data, at: e.at };
  } catch {
    return null;
  }
}

/** Read a cached value (or null when missing / expired / unparseable). */
export function readCache<T>(key: string, ttlMs = DEFAULT_TTL_MS): T | null {
  return readCacheEntry<T>(key, ttlMs)?.data ?? null;
}

/** Write a value to the cache (best-effort; ignores quota/serialization errors). */
export function writeCache<T>(key: string, data: T, epoch = sessionEpoch): void {
  if (epoch !== sessionEpoch) return;
  const store = ls();
  if (!store) return;
  try {
    store.setItem(PREFIX + key, JSON.stringify({ v: 1, at: Date.now(), data } satisfies Entry<T>));
  } catch {
    /* quota exceeded / non-serializable — caching is best-effort */
  }
}

/** Drop a single cached key. */
export function clearCache(key: string): void {
  ls()?.removeItem(PREFIX + key);
}

/** Wipe ALL cached entries (sign-out / invalid keys / admin "clear cache"). */
export function clearAllCache(): void {
  sessionEpoch++;
  const store = ls();
  if (!store) return;
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k && k.startsWith(PREFIX)) keys.push(k);
  }
  for (const k of keys) store.removeItem(k);
}
